import { memo } from 'react';

import type { FormatBundle } from '../../format/bundle';
import { parseValue } from '../../format/instant';
import { useFormat } from '../useFormat';
import { DEFAULT_EMPTY_VALUE, type EmptyProps, isBlankNumber } from './shared';

/** The styles that render a point in time. The other styles are lengths, and are handled apart. */
export type InstantStyle =
  | 'date'
  | 'dateLong'
  | 'time'
  | 'dateTime'
  | 'relativeTime'
  | 'calendarTime'
  | 'recency';

/** The two styles that measure elapsed time, and so take a count of seconds. */
type SecondsStyle = 'duration' | 'clockDuration';

/** A point in time, in any of the shapes an endpoint might send one. */
type InstantProps = EmptyProps & {
  /** Defaults to `dateTime`. */
  readonly style?: InstantStyle;
  /**
   * An instant. A string has to be ISO 8601, and one carrying no offset is read as UTC rather
   * than as the host's local time; any other shape renders as `emptyValue`.
   */
  readonly value: Date | string | number | null | undefined;
  /** Appends the zone abbreviation, as in `3:48 PM EDT`. Read by `time` and `dateTime`. */
  readonly withZone?: boolean;
};

/** An elapsed length, counted in seconds. */
type SecondsProps = EmptyProps & {
  /**
   * `duration` names its units — `1 hour 48 minutes`. `clockDuration` is the digital shape a
   * scrubber or a countdown wants — `1:48:00`.
   */
  readonly style: SecondsStyle;
  /** A length of time, in seconds. */
  readonly value: number | null | undefined;
  /** Unit wording for `duration`. Defaults to `narrow` (`5h 38m`); use `long` for `5 hours 38 minutes`. */
  readonly unitDisplay?: 'long' | 'short' | 'narrow';
};

/** A time of day that arrives already written as a string. */
type PlainTimeProps = EmptyProps & {
  /**
   * Reads an ISO local time — `08:00` — which belongs to no day and so needs no instant.
   */
  readonly style: 'plainTime';
  /** `HH:mm` or `HH:mm:ss`. */
  readonly value: string | null | undefined;
};

/** A length that was written down rather than measured. */
type IsoDurationProps = EmptyProps & {
  /**
   * Reads an ISO-8601 calendar duration — `P39M`, whose months and years have no fixed length in
   * seconds and so cannot go through `duration`.
   */
  readonly style: 'isoDuration';
  /** An ISO-8601 duration, such as `P39M`. */
  readonly value: string | null | undefined;
  /** How many units to name, largest first. Defaults to two. */
  readonly fields?: number;
};

/**
 * The two styles whose payload is already a string, and is read rather than converted.
 *
 * Two arms rather than one so that `fields` reaches only the style that has units to count. A
 * `plainTime` has one field by definition, and offering it the prop would invite a caller to set it.
 */
type TextProps = PlainTimeProps | IsoDurationProps;

/**
 * Three shapes rather than one, because a style decides what a value may be: seven take an instant,
 * two take a number of seconds, and two take a string. A single `value` covering all of them would
 * let `<DateTime style="duration" />` accept a `Date` and fail at runtime.
 */
export type DateTimeProps = InstantProps | SecondsProps | TextProps;

/**
 * Which arm a caller passed.
 *
 * Written as predicates rather than as `props.style === …` comparisons because narrowing an
 * intersection by two literals at once leaves the other arms in play, and the instant arm's
 * optional `style` then reads as absent from the union.
 */
const isSeconds = (props: DateTimeProps): props is SecondsProps =>
  props.style === 'duration' || props.style === 'clockDuration';

const isText = (props: DateTimeProps): props is TextProps =>
  props.style === 'plainTime' || props.style === 'isoDuration';

const formatInstant = (
  format: FormatBundle,
  style: InstantStyle,
  date: Date,
  withZone: boolean | undefined
): string => {
  switch (style) {
    case 'date':
      return format.date(date);
    case 'dateLong':
      return format.dateLong(date);
    case 'time':
      return format.time(date, { withZone });
    case 'relativeTime':
      return format.relativeTime(date);
    case 'calendarTime':
      return format.calendarTime(date);
    case 'recency':
      return format.recency(date);
    case 'dateTime':
      return format.dateTime(date, { withZone });
  }
};

/**
 * What a value carrying a day and no time can be asked for.
 *
 * `date` and `dateTime` both render the day: there is no time in the payload, and the midnight the
 * parser supplied to stand in for one is an artefact rather than a fact worth showing. Asking such
 * a value for a `time` has no answer, so it renders as empty — a `12:00 AM` that no endpoint sent
 * is the kind of detail a reader will otherwise plan around.
 *
 * `calendarTime` and `recency` are empty for the same reason: both name a time of day, and neither
 * can name one the payload does not carry.
 */
const formatDay = (
  format: FormatBundle,
  style: InstantStyle,
  date: Date
): string | undefined => {
  switch (style) {
    case 'date':
    case 'dateTime':
      return format.plainDate(date);
    case 'dateLong':
      return format.plainDateLong(date);
    case 'relativeTime':
      return format.relativeTime(date);
    case 'time':
    case 'calendarTime':
    case 'recency':
      return undefined;
  }
};

/**
 * A date, a time, both, how long ago, or how long.
 *
 * The four instant styles render inside `<time dateTime>`, so that what a screen reader and a
 * scraper get does not depend on the reader's field order or clock — and so `2 hours ago` still
 * says which two hours. The attribute carries the ISO instant, except for a value that named a
 * day, where it carries the day: `2026-01-15` rather than a midnight in some zone. Both are legal
 * `datetime` values, and writing an instant for a day would hand a scraper the zone question the
 * rendered text just avoided.
 *
 * The length styles render bare: a duration is not a point in time, and `<time>` cannot express
 * one whose months carry no fixed length.
 */
const DateTime = memo((props: DateTimeProps): JSX.Element => {
  const format = useFormat();
  const { emptyValue = DEFAULT_EMPTY_VALUE } = props;

  if (isSeconds(props)) {
    if (isBlankNumber(props.value)) return <>{emptyValue}</>;

    return (
      <>
        {props.style === 'duration'
          ? format.duration(props.value, {
              style: props.unitDisplay ?? 'narrow',
            })
          : format.clockDuration(props.value)}
      </>
    );
  }

  if (isText(props)) {
    const written = props.value?.trim();

    if (!written) return <>{emptyValue}</>;

    return (
      <>
        {props.style === 'plainTime'
          ? format.plainTime(written)
          : format.isoDuration(written, { fields: props.fields })}
      </>
    );
  }

  const { value, style = 'dateTime', withZone } = props;
  const parsed = parseValue(value);

  if (parsed === undefined) return <>{emptyValue}</>;

  const { date, day } = parsed;
  const text =
    day === undefined
      ? formatInstant(format, style, date, withZone)
      : formatDay(format, style, date);

  if (text === undefined) return <>{emptyValue}</>;

  return <time dateTime={day ?? date.toISOString()}>{text}</time>;
});

DateTime.displayName = 'DateTime';

export default DateTime;
